import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, Spinner } from "@/components/Common";
import {
  LayoutDashboard, Mail, Send, MessageSquareReply, ShieldCheck,
  RefreshCw, Clock, Building2, TrendingUp,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  AreaChart, Area,
} from "recharts";
import { toast } from "sonner";

function Countdown({ nextRun }) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    if (!nextRun) return;
    const tick = () => {
      const diff = new Date(nextRun).getTime() - Date.now();
      if (diff <= 0) { setLeft("in corso..."); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setLeft(`${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextRun]);
  return <span className="font-mono text-amber-400 text-lg font-semibold" data-testid="scraper-countdown">{left || "--:--:--"}</span>;
}

const KPIS = [
  { key: "emails_found", label: "Email Trovate", icon: Mail, color: "text-amber-400", bg: "bg-amber-500/10" },
  { key: "hiring_count", label: "Hotel che Assumono", icon: Building2, color: "text-sky-400", bg: "bg-sky-500/10" },
  { key: "sent_count", label: "Candidature Inviate", icon: Send, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  { key: "replied_count", label: "Risposte Ricevute", icon: MessageSquareReply, color: "text-purple-400", bg: "bg-purple-500/10" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, st] = await Promise.all([api.get("/dashboard/stats"), api.get("/scraper/status")]);
      setStats(s.data); setStatus(st.data);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const runScraper = async () => {
    setScraping(true);
    try {
      await api.post("/scraper/run?limit=12");
      toast.success("Scraper avviato manualmente", { description: "Ricerca email hotel in corso..." });
      setTimeout(load, 4000);
    } catch { toast.error("Errore nell'avvio dello scraper"); }
    finally { setTimeout(() => setScraping(false), 4000); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Spinner className="w-8 h-8" /></div>;

  const ab = stats?.anti_bot || {};

  return (
    <div className="p-6 max-w-[1500px]">
      <PageHeader
        title="Panoramica & Statistiche"
        subtitle="Monitoraggio candidature cuoco/chef negli hotel della Germania"
        icon={LayoutDashboard}
        actions={
          <button data-testid="run-scraper-btn" onClick={runScraper} disabled={scraping}
            className="btn-copper px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${scraping ? "spin-slow" : ""}`} />
            {scraping ? "Scraping..." : "Forza Aggiornamento Ora"}
          </button>
        }
      />

      {/* Scraper status banner */}
      <div className="card p-5 mb-5 fade-up flex flex-wrap items-center justify-between gap-4 border-amber-500/20">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center pulse-glow">
            <Clock className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-gray-500">Prossimo aggiornamento automatico (ogni 2 ore)</div>
            <Countdown nextRun={status?.next_run} />
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Ultimo ciclo</div>
            <div className="text-sm text-gray-200">{status?.last_run ? new Date(status.last_run).toLocaleString("it-IT") : "—"}</div>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-400 font-medium">Anti-Bot {ab.bypass_rate ?? 100}%</span>
          </div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {KPIS.map((k, i) => (
          <div key={k.key} className="card p-5 fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`kpi-${k.key}`}>
            <div className="flex items-center justify-between">
              <div className={`w-10 h-10 rounded-lg ${k.bg} flex items-center justify-center`}>
                <k.icon className={`w-5 h-5 ${k.color}`} />
              </div>
              <TrendingUp className="w-4 h-4 text-gray-600" />
            </div>
            <div className="mt-4 text-3xl font-head font-extrabold text-gray-100">{stats?.[k.key] ?? 0}</div>
            <div className="text-sm text-gray-500 mt-1">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="card p-6 lg:col-span-2">
          <h3 className="font-head font-semibold text-gray-200 mb-4">Lead per Regione (Bundesländer)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats?.region_data || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="region" tick={{ fill: "#9CA3AF", fontSize: 11 }} angle={-20} textAnchor="end" height={60} interval={0} />
              <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#13161C", border: "1px solid #ffffff20", borderRadius: 10, color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="count" fill="#D97706" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card p-6">
          <h3 className="font-head font-semibold text-gray-200 mb-4">Candidature Inviate (7gg)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={stats?.sent_series?.length ? stats.sent_series : [{ day: "-", count: 0 }]}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: "#9CA3AF", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#13161C", border: "1px solid #ffffff20", borderRadius: 10, color: "#fff" }} />
              <Area type="monotone" dataKey="count" stroke="#10B981" fill="url(#g1)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Anti-bot detail */}
      <div className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-emerald-400" />
          <h3 className="font-head font-semibold text-gray-200">Modulo Anti-Bot & Scraping (ultimo ciclo)</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            ["Fonti scansionate", ab.sites_scanned ?? 0],
            ["Richieste HTTP", ab.requests ?? 0],
            ["User-Agent ruotati", ab.user_agents_rotated ?? 0],
            ["Throttling incontrati", ab.blocked_hits ?? 0],
            ["Bypass riusciti", ab.bypassed ?? 0],
            ["Tasso bypass", `${ab.bypass_rate ?? 100}%`],
          ].map(([l, v]) => (
            <div key={l} className="bg-[#0E1015] rounded-lg p-3 border border-white/5">
              <div className="text-xl font-head font-bold text-gray-100">{v}</div>
              <div className="text-[11px] text-gray-500 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Source breakdown */}
      <div className="card p-6 mt-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-semibold text-gray-200">Fonti dei Lead & Offerte</h3>
          <span className="text-xs text-gray-500">{stats?.portal_offers ?? 0} offerte da portali</span>
        </div>
        <div className="space-y-2.5">
          {(stats?.source_data || []).length === 0 ? (
            <div className="text-sm text-gray-500">Nessun dato ancora</div>
          ) : (stats.source_data).map((s) => {
            const max = Math.max(...stats.source_data.map((x) => x.count), 1);
            const pct = Math.round((s.count / max) * 100);
            const color = s.source.includes("Arbeitsagentur") ? "bg-sky-500"
              : s.source.includes("Facebook") ? "bg-indigo-500" : "bg-amber-500";
            return (
              <div key={s.source} className="flex items-center gap-3" data-testid={`source-row`}>
                <div className="w-40 text-xs text-gray-400 truncate shrink-0">{s.source}</div>
                <div className="flex-1 h-2.5 bg-white/5 rounded-full overflow-hidden">
                  <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <div className="w-10 text-right text-sm text-gray-200 font-mono">{s.count}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
